import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Badge,
    Grid,
    withTheme,
    Button,
    ClickAwayListener,
    Paper,
    makeStyles,
    Popper,
    Box,
    Typography
} from '@material-ui/core';
import { useHistory, useLocation } from 'react-router';
import { MODAL_TYPE, useModals } from '../hooks/useModal';
import GuestsTable from './GuestsTable';
import { useGuestFields } from '../hooks/useGuestFields';
import { useNotify } from '../hooks/useNotify';
import { CartIcon, AgentNotesIcon, WarningIcon } from '../icons';
import { resetUrlFilters } from '../pages/activitycards/components/urlFiltering';
import { getInitialLockHolders } from '../tools/softlock';

const useStyles = makeStyles({
    dropDown: {
        padding: '10px',
        fontSize: '14px',
        zIndex: 10,
        whiteSpace: 'pre-line'
    }
});

const GuestHeader = ({
    user,
    cartItemCount,
    customer,
    theme,
    loginAsCustomerWithId,
    guestTableOpen,
    setGuestTableOpen,
    createGuest
}) => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { showModal } = useModals();
    const { notifyError } = useNotify();
    const [guest_fields, headerGuestFields] = useGuestFields();
    const [dropdownVisible, setDropdownVisible] = useState(false);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const id = dropdownVisible ? 'internal-notes-popper' : undefined;

    const handleClose = event => {
        setDropdownVisible(!dropdownVisible);
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    useEffect(() => {
        // on location change, close the table
        setGuestTableOpen(false);
    }, [location, setGuestTableOpen]);

    const initialLockHolders = getInitialLockHolders();

    // only show this to agents
    if (!user || (user.role !== 'agent' && user.role !== 'admin')) {
        return null;
    }

    const guestTableDropdown = (
        <div
            className={`guest-table-container ${guestTableOpen ? 'is-open' : ''}`}
            style={{ borderColor: theme.palette.secondary.main }}
        >
            {guestTableOpen && (
                <GuestsTable
                    user={user}
                    customer={customer}
                    loginAsCustomerWithId={loginAsCustomerWithId}
                    closeGuestTable={() => window.setGuestTableOpen(false)}
                    cartItemCount={cartItemCount}
                />
            )}
        </div>
    );

    // if there is no "customer" selected
    if (!customer) {
        return (
            <Grid className="guest-header">
                <div>
                    <div
                        className="guest-bar"
                        style={{ backgroundColor: theme.palette.secondary.main }}
                    >
                        <div>
                            <Link
                                style={{ color: '#fff' }}
                                to="/golf"
                                onClick={ev => {
                                    ev.preventDefault();
                                    window.setGuestTableOpen(!guestTableOpen);
                                }}
                            >
                                Guest List
                            </Link>
                        </div>
                        <div className="guestBarRight">
                            <Button
                                className="btn-squared"
                                color="primary"
                                size="small"
                                onClick={() => {
                                    showModal(MODAL_TYPE.CREATE_OR_UPDATE_GUEST, {
                                        isAgent: true,
                                        guest_fields,
                                        handleSubmit: data => {
                                            createGuest(
                                                data.firstname,
                                                data.lastname,
                                                data.phone,
                                                data.email,
                                                data.additional_emails,
                                                data.guest_field_info,
                                                data.arrival_date,
                                                data.departure_date,
                                                data.internal_notes,
                                                data.notification,
                                                data.source_id,
                                                // onSuccess
                                                () => {
                                                    showModal(MODAL_TYPE.NONE);
                                                },
                                                // onError
                                                err => {
                                                    notifyError(err);
                                                }
                                            );
                                        }
                                    });
                                }}
                            >
                                Register a new guest
                            </Button>
                        </div>
                    </div>
                    {guestTableDropdown}
                </div>
            </Grid>
        );
    }

    // there is a customer selected
    return (
        <Grid className="guest-header">
            <div>
                <div
                    className="guest-bar"
                    style={{ backgroundColor: theme.palette.secondary.main }}
                >
                    <Grid container spacing={2} className="guest-bar-info">
                        <Grid item>
                            <AgentNotesIcon
                                style={{
                                    opacity: customer.internal_notes ? 1 : 0.3,
                                    cursor: 'pointer'
                                }}
                                onClick={event => handleClose(event)}
                            />
                            <Popper
                                disablePortal
                                id={id}
                                open={dropdownVisible}
                                anchorEl={anchorEl}
                            >
                                <ClickAwayListener onClickAway={event => handleClose(event)}>
                                    <Paper
                                        spacing={2}
                                        className={classes.dropDown}
                                        style={{ textAlign: 'left' }}
                                    >
                                        {customer.internal_notes}
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </Grid>
                        <Grid item>
                            <Link
                                to={`/guests/${customer.id}`}
                                style={{ color: theme.palette.primary.main }}
                            >
                                <b>
                                    {customer.firstname} {customer.lastname}
                                </b>
                            </Link>
                        </Grid>
                        {headerGuestFields.map(headerField => {
                            let item = customer?.guest_field_info?.find(
                                e => e.id === headerField.value
                            );
                            if (!item) {
                                item = {
                                    name: headerField.name,
                                    value: customer[headerField.value]
                                };
                            }

                            if (
                                item.value === '' ||
                                item.value === null ||
                                typeof item.value === 'undefined'
                            ) {
                                return null;
                            }

                            return (
                                <Grid item key={item.id || item.name}>
                                    <div className="guest-header-field">
                                        <span>{item.name}</span>
                                        {item.value?.toString()}
                                    </div>
                                </Grid>
                            );
                        })}
                        <Grid item>
                            <Link
                                style={{ color: '#fff' }}
                                to="/"
                                onClick={() => {
                                    // this is a logout
                                    loginAsCustomerWithId(
                                        null,
                                        showModal,
                                        () => {},
                                        () => {
                                            history.replace(resetUrlFilters());
                                        }
                                    );
                                }}
                            >
                                Log Out
                            </Link>
                        </Grid>
                    </Grid>
                    <div className="guestBarRight">
                        <span className="cart-icon-wrapper">
                            <Link to="/cart" style={{ color: '#fff' }}>
                                <Badge
                                    badgeContent={cartItemCount}
                                    color="primary"
                                    invisible={cartItemCount === 0}
                                >
                                    <CartIcon />
                                </Badge>
                            </Link>
                        </span>
                    </div>
                </div>
                {initialLockHolders && initialLockHolders.length > 0 && (
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            border: '1px solid' + theme.palette.warning.light,
                            padding: '5px 10px',
                            backgroundColor: theme.palette.warning.light
                        }}
                    >
                        <WarningIcon style={{ verticalAlign: 'middle', marginRight: 5 }} />
                        <Typography>
                            {`This guest is currently being edited by ${
                                initialLockHolders.length
                            } another agent${initialLockHolders.length !== 1 ? 's' : ''}.`}
                        </Typography>
                    </Box>
                )}
                {guestTableDropdown}
            </div>
        </Grid>
    );
};

export default withTheme(GuestHeader);
